import { Doctor } from "./doctorService";
import apiService from "./apiService";

export interface DepartmentComplaintsConfig {
  [deptKey: string]: {
    name: string;
    description: string;
    icon?: string;
    complaints: string[];
  };
}

export interface OPDSchedulingSettings {
  defaultAppointmentType: string;
  defaultChannel: string;
  defaultSlotDurationMinutes: number;
  autoAssignRooms: boolean;
  enableSlotGeneration: boolean;
}

export interface PublicBookingOptions {
  visitModes: Array<"Physical" | "Online">;
  appointmentCategories: Array<"Consultation" | "Follow-up" | "Routine Checkup" | "Emergency" | "Other">;
  doctorSelection: "patient_choice" | "first_available";
  showDepartmentSelection: boolean;
  onlinePaymentEnabled?: boolean;
  onlinePaymentProvider?: "PineLabsOnline" | "RazorpayOnline";
  onlinePaymentUnavailableReasons?: string[];
  onlineConsultationPaymentRequired?: boolean;
  payAtHospitalEnabled?: boolean;
}

export const DEFAULT_PUBLIC_BOOKING_OPTIONS: PublicBookingOptions = {
  payAtHospitalEnabled: true,
  visitModes: ["Physical", "Online"],
  appointmentCategories: ["Consultation", "Follow-up", "Routine Checkup", "Emergency", "Other"],
  doctorSelection: "patient_choice",
  showDepartmentSelection: true,
};

/**
 * OPD encounter / billing policy. Backed by dotted `settings` keys:
 *   prescription.documentation_window_days
 *   followup.window_days / .fee_percent / .max_count / .same_doctor_only
 *   dialysis.default_doctor_id -- doctor on dialysis sessions sold on a Visit bill
 *   dialysis.require_clearance -- OPD dialysis needs an active nephrology clearance
 */
export interface OpdBillingPolicySettings {
  prescriptionWindowDays: number;
  followupWindowDays: number;
  followupFeePercent: number;
  followupMaxCount: number;
  followupSameDoctorOnly: boolean;
  /** Doctor on dialysis sessions sold on a Visit bill; null until set. */
  dialysisDefaultDoctorId: number | null;
  /** OPD dialysis needs an active nephrology clearance (ER / IPD exempt). */
  dialysisRequireClearance: boolean;
}

export interface IpdObservationCorrectionPolicy {
  editWindowMinutes: number;
}

export interface HospitalPreferences {
  language: string;
  dateFormat: "DD/MM/YYYY" | "MM/DD/YYYY" | "YYYY-MM-DD";
  timeFormat: "12h" | "24h";
  currency: string;
  hospitalName: string;
  timezone: string;
  // Detailed Printable & Report Branding (Distinct from legal Billing / GST entities)
  tagline?: string;
  logoUrl?: string;
  address?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  phone?: string;
  emergencyPhone?: string;
  email?: string;
  website?: string;
  registrationNumber?: string;
  accreditation?: string;
  navAutoClose?: boolean;
}

const STORAGE_KEY_COMPLAINTS = "hms_clinical_complaints_v2";
const STORAGE_KEY_SCHEDULING = "hms_opd_scheduling_settings_v2";
const STORAGE_KEY_PREFERENCES = "hms_hospital_preferences_v2";

export const DEFAULT_DEPARTMENT_COMPLAINTS: DepartmentComplaintsConfig = {
  neurology: {
    name: "Neurology",
    description: "Brain, spine, cranial nerves, and peripheral nervous system",
    complaints: [
      "Severe Migraine / Headache",
      "Dizziness & Vertigo",
      "Seizures / Tremors",
      "Numbness & Tingling",
      "Memory Loss / Confusion",
      "Sleep Disturbance",
      "Neuropathy Follow-up",
      "Facial Weakness / Bell's Palsy",
    ],
  },
  cardiology: {
    name: "Cardiology",
    description: "Heart, coronary circulation, and cardiovascular disorders",
    complaints: [
      "Chest Pain / Angina",
      "Shortness of Breath (Dyspnea)",
      "Heart Palpitations",
      "High Blood Pressure (Hypertension)",
      "Leg Swelling (Edema)",
      "Dizziness / Syncope",
      "Post-Cardiac Review",
      "Fatigue on Exertion",
    ],
  },
  orthopedics: {
    name: "Orthopedics & Joint Care",
    description: "Bones, joints, ligaments, tendons, and spine disorders",
    complaints: [
      "Severe Joint Pain",
      "Chronic Lower Back Pain",
      "Knee Pain & Stiffness",
      "Fracture / Post-Op Review",
      "Sports Injury / Ligament Sprain",
      "Neck & Shoulder Pain",
      "Arthritis Management",
      "Difficulty Walking / Gait Issue",
    ],
  },
  pediatrics: {
    name: "Pediatrics & Child Care",
    description: "Infant, child, and adolescent medical care",
    complaints: [
      "High Fever & Chills",
      "Persistent Cough & Cold",
      "Vomiting & Diarrhea",
      "Routine Immunization / Vaccination",
      "Growth & Development Check",
      "Ear Ache / Infection",
      "Loss of Appetite",
      "Skin Rash / Eczema",
    ],
  },
  dermatology: {
    name: "Dermatology & Skin Care",
    description: "Skin, hair, nails, and cutaneous disorders",
    complaints: [
      "Itchy Skin Rash / Lesions",
      "Severe Acne & Breakouts",
      "Hair Fall / Alopecia",
      "Eczema / Psoriasis Flare-up",
      "Fungal Infection / Ringworm",
      "Allergic Reaction / Hives",
      "Pigmentation & Dark Spots",
      "Mole / Growth Evaluation",
    ],
  },
  ent: {
    name: "ENT (Otolaryngology)",
    description: "Ear, nose, throat, sinuses, and neck disorders",
    complaints: [
      "Ear Pain / Fluid Discharge",
      "Sore Throat & Difficulty Swallowing",
      "Hearing Loss / Tinnitus",
      "Nasal Congestion & Blockage",
      "Chronic Sinusitis",
      "Tonsil Swelling",
      "Voice Hoarseness",
      "Nosebleeds (Epistaxis)",
    ],
  },
  ophthalmology: {
    name: "Ophthalmology & Eye Care",
    description: "Eye diseases, vision correction, and ocular surgery",
    complaints: [
      "Blurry / Diminished Vision",
      "Eye Redness & Irritation",
      "Eye Strain & Frequent Headaches",
      "Excessive Tearing / Dry Eyes",
      "Cataract Evaluation",
      "Foreign Body Sensation",
      "Floaters / Flashes",
      "Glaucoma Review",
    ],
  },
  gynecology: {
    name: "Gynecology & Obstetrics",
    description: "Female reproductive health, pregnancy, and childbirth",
    complaints: [
      "Routine Prenatal / Antenatal Checkup",
      "Pelvic & Lower Abdominal Pain",
      "Irregular Menstrual Cycle",
      "PCOD / PCOS Review",
      "Ultrasound & Scan Review",
      "Post-Partum Follow-up",
      "Menopausal Symptoms",
      "Abnormal Bleeding",
    ],
  },
  urology: {
    name: "Urology",
    description: "Urinary tract, prostate, and male reproductive system",
    complaints: [
      "Burning Sensation on Urination (Dysuria)",
      "Kidney Stone Pain (Flank Pain)",
      "Frequent Urination / Nocturia",
      "Blood in Urine (Hematuria)",
      "Prostate Health Review",
      "Urinary Incontinence",
      "Weak Urine Stream",
    ],
  },
  nephrology: {
    name: "Nephrology & Dialysis",
    description: "Kidney disease, dialysis management, and renal care",
    complaints: [
      "Dialysis Session Review",
      "High Creatinine / Kidney Check",
      "Generalized Body Swelling",
      "Protein in Urine (Proteinuria)",
      "Renal Diet & Medication Review",
      "Electrolyte Imbalance",
      "Fistula Site Check",
    ],
  },
  dental: {
    name: "Dentistry & Oral Care",
    description: "Teeth, gums, jaw, and oral maxillofacial care",
    complaints: [
      "Acute Toothache",
      "Bleeding / Swollen Gums",
      "Root Canal Follow-up",
      "Dental Cavity / Filling",
      "Wisdom Tooth Pain",
      "Teeth Cleaning & Scaling",
      "Orthodontic / Braces Review",
      "Jaw Joint (TMJ) Pain",
    ],
  },
  pulmonology: {
    name: "Pulmonology & Respiratory",
    description: "Lungs, airways, and breathing disorders",
    complaints: [
      "Chronic Productive Cough",
      "Wheezing & Asthma Attack",
      "Shortness of Breath (Dyspnea)",
      "Chest Congestion",
      "Post-COVID Respiratory Check",
      "Sleep Apnea / Heavy Snoring",
      "Hemoptysis (Coughing Blood)",
    ],
  },
  gastroenterology: {
    name: "Gastroenterology & Digestive",
    description: "Esophagus, stomach, intestines, liver, and pancreas",
    complaints: [
      "Severe Acid Reflux / GERD",
      "Abdominal Cramps & Pain",
      "Bloating & Indigestion",
      "Chronic Constipation / Diarrhea",
      "Nausea & Vomiting",
      "Liver Function Review",
      "Rectal Bleeding / Piles",
      "Jaundice Check",
    ],
  },
  oncology: {
    name: "Oncology & Cancer Care",
    description: "Cancer diagnosis, chemotherapy, and tumor surveillance",
    complaints: [
      "Chemotherapy Follow-up",
      "Post-Treatment Surveillance Review",
      "Unexplained Weight Loss",
      "Persistent Fatigue & Weakness",
      "Lab & Scan Review",
      "Pain Management Consultation",
      "Lump / Mass Evaluation",
    ],
  },
  psychiatry: {
    name: "Psychiatry & Mental Health",
    description: "Mood, anxiety, behavioral, and psychiatric health",
    complaints: [
      "Severe Anxiety & Panic Attacks",
      "Persistent Low Mood / Depression",
      "Insomnia / Sleep Disturbance",
      "Stress & Burnout Consultation",
      "Psychiatric Medication Review",
      "ADHD / Focus Issues",
      "Bipolar Disorder Follow-up",
    ],
  },
  endocrinology: {
    name: "Endocrinology & Diabetes",
    description: "Hormone disorders, diabetes, thyroid, and metabolism",
    complaints: [
      "Diabetes / HbA1c Review",
      "Thyroid Swelling / Hormone Check",
      "Unexplained Weight Gain / Loss",
      "High Blood Sugar Fluctuations",
      "Osteoporosis Evaluation",
      "Hormonal Imbalance Check",
    ],
  },
  general: {
    name: "General Medicine",
    description: "Primary care, comprehensive checkups, and general physician",
    complaints: [
      "Fever & Chills",
      "Cough & Cold",
      "Chest Pain / Heaviness",
      "Severe Headache",
      "Abdominal Pain",
      "Routine Health Checkup",
      "Follow-up Consultation",
      "Body Ache & General Weakness",
      "Blood Pressure Check",
      "Annual Health Screening",
    ],
  },
};

export const DEFAULT_OPD_BILLING_POLICY: OpdBillingPolicySettings = {
  prescriptionWindowDays: 2,
  followupWindowDays: 7,
  followupFeePercent: 0,
  followupMaxCount: 1,
  followupSameDoctorOnly: true,
  dialysisDefaultDoctorId: null,
  dialysisRequireClearance: false,
};

export const DEFAULT_OPD_SCHEDULING: OPDSchedulingSettings = {
  defaultAppointmentType: "Consultation",
  defaultChannel: "Scheduled",
  defaultSlotDurationMinutes: 15,
  autoAssignRooms: true,
  enableSlotGeneration: true,
};

export const DEFAULT_HOSPITAL_PREFERENCES: HospitalPreferences = {
  language: "en",
  dateFormat: "DD/MM/YYYY",
  timeFormat: "12h",
  currency: "$",
  hospitalName: "DaAFYA Medical Center",
  tagline: "Multi-Speciality Healthcare & Clinical Research Institute",
  logoUrl: "",
  address: "Healthcare Blvd, Medical District",
  city: "Bangalore",
  state: "Karnataka",
  postalCode: "560001",
  country: "India",
  phone: "+91 (800) 123-4567",
  emergencyPhone: "108 / +91 (800) 999-9999",
  email: "care@yourhospital.com",
  website: "https://yourhospital.com",
  registrationNumber: "HOSP-MED-2026-0891",
  accreditation: "NABH & JCI Accredited Hospital",
  timezone: "UTC+05:30",
  navAutoClose: true,
};

/**
 * Specialty alias keywords matcher
 */
const SPECIALTY_ALIAS_MAP: Record<string, string[]> = {
  neurology: ["neurol", "brain", "neuro", "nerve", "stroke", "spine", "headache", "epilepsy"],
  cardiology: ["cardio", "heart", "coronary", "vascular", "cardiac", "angio", "hypertens"],
  orthopedics: ["ortho", "bone", "joint", "fracture", "musculoskelet", "arthr", "knee", "spine"],
  pediatrics: ["pediatr", "paediatr", "child", "infant", "neonat", "baby", "kid"],
  dermatology: ["derma", "skin", "cosmeto", "hair", "nail", "cutaneous", "trichol"],
  ent: ["ent", "ear", "nose", "throat", "otolaryng", "laryng", "rhinol", "audiol"],
  ophthalmology: ["ophthalm", "eye", "optic", "ocular", "vision", "cataract", "retina", "glaucoma", "optomet"],
  gynecology: ["gynec", "gynaec", "obstet", "ob/gyn", "obgyn", "matern", "women", "fertility", "ivf"],
  urology: ["uro", "urinary", "prostate", "bladder", "androl", "renal calculi", "stone"],
  nephrology: ["nephro", "dialysis", "renal", "kidney", "creatinine"],
  dental: ["dent", "oral", "tooth", "teeth", "maxillofac", "orthodont", "periodont", "endodont", "implant"],
  pulmonology: ["pulmon", "chest", "respirat", "lung", "asthma", "bronch", "tb", "sleep apnea"],
  gastroenterology: ["gastro", "digest", "hepat", "liver", "endoscop", "colon", "gi", "pancreas", "biliary"],
  oncology: ["onco", "cancer", "tumor", "chemo", "radiat", "hematol-onco", "malignan"],
  psychiatry: ["psychiat", "psychol", "mental", "behavior", "counsel", "therapy", "addiction"],
  endocrinology: ["endocrin", "diabet", "metabol", "thyroid", "hormon", "obesity"],
  general: ["general", "internal", "physician", "family", "medicine", "practitioner", "primary", "mbbs", "md"],
};

class ClinicalSettingsService {
  private complaintsCache: DepartmentComplaintsConfig | null = null;
  private schedulingCache: OPDSchedulingSettings | null = null;
  private preferencesCache: HospitalPreferences | null = null;

  constructor() {
    this.init();
  }

  private init() {
    try {
      const stored = localStorage.getItem(STORAGE_KEY_COMPLAINTS);
      if (stored) {
        this.complaintsCache = JSON.parse(stored);
      } else {
        this.complaintsCache = DEFAULT_DEPARTMENT_COMPLAINTS;
        localStorage.setItem(STORAGE_KEY_COMPLAINTS, JSON.stringify(DEFAULT_DEPARTMENT_COMPLAINTS));
      }

      const storedSched = localStorage.getItem(STORAGE_KEY_SCHEDULING);
      if (storedSched) {
        this.schedulingCache = JSON.parse(storedSched);
      } else {
        this.schedulingCache = DEFAULT_OPD_SCHEDULING;
        localStorage.setItem(STORAGE_KEY_SCHEDULING, JSON.stringify(DEFAULT_OPD_SCHEDULING));
      }

      const storedPrefs = localStorage.getItem(STORAGE_KEY_PREFERENCES);
      if (storedPrefs) {
        this.preferencesCache = JSON.parse(storedPrefs);
      } else {
        this.preferencesCache = DEFAULT_HOSPITAL_PREFERENCES;
        localStorage.setItem(STORAGE_KEY_PREFERENCES, JSON.stringify(DEFAULT_HOSPITAL_PREFERENCES));
      }
    } catch (e) {
      console.warn("Failed to load clinical settings from localStorage, using defaults:", e);
      this.complaintsCache = DEFAULT_DEPARTMENT_COMPLAINTS;
      this.schedulingCache = DEFAULT_OPD_SCHEDULING;
      this.preferencesCache = DEFAULT_HOSPITAL_PREFERENCES;
    }
  }

  // Resolves doctor specialty accurately without failing on variations (e.g. Neurologist, Pediatrician, Orthopedic Surgeon)
  public resolveDepartmentKey(specialtyOrDept?: string): string {
    if (!specialtyOrDept) return "general";
    const text = specialtyOrDept.toLowerCase().trim();

    for (const [deptKey, aliases] of Object.entries(SPECIALTY_ALIAS_MAP)) {
      for (const alias of aliases) {
        if (text.includes(alias)) {
          return deptKey;
        }
      }
    }

    return "general";
  }

  public getComplaintsForDoctor(doctor?: Doctor | null): string[] {
    const all = this.getAllComplaints();
    if (!doctor) {
      return all.general?.complaints || DEFAULT_DEPARTMENT_COMPLAINTS.general.complaints;
    }

    const specialty = doctor.speciality || (doctor as any).department_name || (typeof (doctor as any).department === 'string' ? (doctor as any).department : (doctor as any).department?.name) || "";
    const deptKey = this.resolveDepartmentKey(specialty);

    const config = all[deptKey];
    if (config && config.complaints && config.complaints.length > 0) {
      return config.complaints;
    }

    return all.general?.complaints || DEFAULT_DEPARTMENT_COMPLAINTS.general.complaints;
  }

  public getAllComplaints(): DepartmentComplaintsConfig {
    if (!this.complaintsCache) {
      this.init();
    }
    return this.complaintsCache || DEFAULT_DEPARTMENT_COMPLAINTS;
  }

  public saveComplaints(config: DepartmentComplaintsConfig): void {
    this.complaintsCache = config;
    try {
      localStorage.setItem(STORAGE_KEY_COMPLAINTS, JSON.stringify(config));
      window.dispatchEvent(new CustomEvent("hms_clinical_complaints_updated", { detail: config }));
    } catch (e) {
      console.error("Error saving clinical complaints:", e);
    }
  }

  public addComplaint(deptKey: string, complaint: string): void {
    const all = { ...this.getAllComplaints() };
    if (!all[deptKey]) {
      all[deptKey] = {
        name: deptKey.charAt(0).toUpperCase() + deptKey.slice(1),
        description: "",
        complaints: [],
      };
    }
    const clean = complaint.trim();
    if (clean && !all[deptKey].complaints.includes(clean)) {
      all[deptKey] = {
        ...all[deptKey],
        complaints: [...all[deptKey].complaints, clean],
      };
      this.saveComplaints(all);
    }
  }

  public removeComplaint(deptKey: string, complaint: string): void {
    this.deleteComplaint(deptKey, complaint);
  }

  public deleteComplaint(deptKey: string, complaint: string): void {
    const all = { ...this.getAllComplaints() };
    if (all[deptKey]) {
      all[deptKey] = {
        ...all[deptKey],
        complaints: all[deptKey].complaints.filter((c) => c !== complaint),
      };
      this.saveComplaints(all);
    }
  }

  public resetComplaintsToDefaults(): void {
    this.saveComplaints(DEFAULT_DEPARTMENT_COMPLAINTS);
  }

  /**
   * Save complaints configuration directly to tenant database
   */
  public async saveComplaintsRemote(config: DepartmentComplaintsConfig): Promise<void> {
    this.saveComplaints(config);
    try {
      await apiService.put("/settings", {
        clinical_complaints: JSON.stringify(config),
      });
    } catch (e) {
      console.error("Error persisting clinical complaints to tenant database:", e);
      throw e;
    }
  }

  public getSchedulingSettings(): OPDSchedulingSettings {
    if (!this.schedulingCache) {
      this.init();
    }
    return this.schedulingCache || DEFAULT_OPD_SCHEDULING;
  }

  public saveSchedulingSettings(settings: OPDSchedulingSettings): void {
    this.schedulingCache = settings;
    try {
      localStorage.setItem(STORAGE_KEY_SCHEDULING, JSON.stringify(settings));
      window.dispatchEvent(new CustomEvent("hms_opd_scheduling_updated", { detail: settings }));
    } catch (e) {
      console.error("Error saving scheduling settings:", e);
    }
  }

  /**
   * Save OPD scheduling settings directly to tenant database
   */
  public async saveSchedulingSettingsRemote(settings: OPDSchedulingSettings): Promise<void> {
    this.saveSchedulingSettings(settings);
    try {
      await apiService.put("/settings", {
        opd_scheduling_settings: JSON.stringify(settings),
        default_slot_duration_minutes: String(settings.defaultSlotDurationMinutes),
        opd_default_appointment_type: settings.defaultAppointmentType,
        opd_default_channel: settings.defaultChannel,
        opd_auto_assign_rooms: String(settings.autoAssignRooms),
        opd_enable_slot_generation: String(settings.enableSlotGeneration),
      });
    } catch (e) {
      console.error("Error persisting scheduling settings to tenant database:", e);
      throw e;
    }
  }

  async getBookingPaymentPolicy(): Promise<{ payAtHospitalEnabled: boolean }> {
    const response = await apiService.get('/settings/public-booking/payment-policy');
    return response.data;
  }

  async saveBookingPaymentPolicy(payAtHospitalEnabled: boolean): Promise<{ payAtHospitalEnabled: boolean }> {
    const response = await apiService.put('/settings/public-booking/payment-policy', { payAtHospitalEnabled });
    return response.data;
  }

  public async savePublicBookingOptionsRemote(options: PublicBookingOptions): Promise<void> {
    const { payAtHospitalEnabled: _policy, ...bookingOptions } = options;
    await apiService.put("/settings", { public_booking_options: JSON.stringify(bookingOptions) });
  }

  /**
   * OPD encounter / billing policy -> the dotted `settings` keys the backend
   * reads (prescription.documentation_window_days, followup.*).
   */
  public async saveBillingPolicySettingsRemote(
    policy: OpdBillingPolicySettings,
  ): Promise<void> {
    try {
      await apiService.put("/settings", {
        "prescription.documentation_window_days": String(
          Math.max(0, Math.round(policy.prescriptionWindowDays)),
        ),
        "followup.window_days": String(
          Math.max(0, Math.round(policy.followupWindowDays)),
        ),
        "followup.fee_percent": String(
          Math.min(100, Math.max(0, Math.round(policy.followupFeePercent))),
        ),
        "followup.max_count": String(
          Math.max(0, Math.round(policy.followupMaxCount)),
        ),
        "followup.same_doctor_only": policy.followupSameDoctorOnly
          ? "true"
          : "false",
        // "0", not "": empty strings become null and null values are not saved.
      });
    } catch (e) {
      console.error("Error persisting OPD billing policy to tenant database:", e);
      throw e;
    }
  }

  public async getIpdObservationCorrectionPolicy(): Promise<IpdObservationCorrectionPolicy> {
    const response = await apiService.get<{ data: { edit_window_minutes: number } }>(
      "/settings/ipd/vitals-correction-window",
    );
    return { editWindowMinutes: Number(response.data?.edit_window_minutes) || 10 };
  }

  public async saveIpdObservationCorrectionPolicy(policy: IpdObservationCorrectionPolicy): Promise<void> {
    await apiService.put("/settings/ipd/vitals-correction-window", {
      edit_window_minutes: Math.min(1440, Math.max(1, Math.round(policy.editWindowMinutes))),
    });
  }

  public getHospitalPreferences(): HospitalPreferences {
    if (!this.preferencesCache) {
      this.init();
    }
    return this.preferencesCache || DEFAULT_HOSPITAL_PREFERENCES;
  }

  public saveHospitalPreferences(prefs: HospitalPreferences): void {
    this.preferencesCache = prefs;
    try {
      localStorage.setItem(STORAGE_KEY_PREFERENCES, JSON.stringify(prefs));
      window.dispatchEvent(new CustomEvent("hms_hospital_preferences_updated", { detail: prefs }));
    } catch (e) {
      console.error("Error saving hospital preferences:", e);
    }
  }

  /**
   * Fetch all settings directly from the tenant database table `settings`
   */
  public async fetchRemoteSettings(): Promise<{
    preferences: HospitalPreferences;
    scheduling: OPDSchedulingSettings;
    billingPolicy: OpdBillingPolicySettings;
    complaints: DepartmentComplaintsConfig;
    publicBooking: PublicBookingOptions;
  }> {
    try {
      const res = await apiService.get("/settings");
      const data = res?.data || res || {};

      // 1. Hospital Preferences & Detailed Printable Branding
      const currentPrefs = this.getHospitalPreferences();
      const updatedPrefs: HospitalPreferences = {
        ...currentPrefs,
        currency: data.currency || currentPrefs.currency || "USD",
        dateFormat: (data.date_format || currentPrefs.dateFormat || "DD/MM/YYYY") as any,
        timeFormat: (data.time_format || currentPrefs.timeFormat || "12h") as any,
        hospitalName: data.hospital_name || currentPrefs.hospitalName || "DaAFYA Medical Center",
        tagline: data.hospital_tagline !== undefined ? data.hospital_tagline : currentPrefs.tagline,
        logoUrl: data.hospital_logo_url !== undefined ? data.hospital_logo_url : currentPrefs.logoUrl,
        address: data.hospital_address !== undefined ? data.hospital_address : currentPrefs.address,
        city: data.hospital_city !== undefined ? data.hospital_city : currentPrefs.city,
        state: data.hospital_state !== undefined ? data.hospital_state : currentPrefs.state,
        postalCode: data.hospital_postal_code !== undefined ? data.hospital_postal_code : currentPrefs.postalCode,
        country: data.hospital_country !== undefined ? data.hospital_country : currentPrefs.country,
        phone: data.hospital_phone !== undefined ? data.hospital_phone : currentPrefs.phone,
        emergencyPhone: data.hospital_emergency_phone !== undefined ? data.hospital_emergency_phone : currentPrefs.emergencyPhone,
        email: data.hospital_email !== undefined ? data.hospital_email : currentPrefs.email,
        website: data.hospital_website !== undefined ? data.hospital_website : currentPrefs.website,
        registrationNumber: data.hospital_registration_number !== undefined ? data.hospital_registration_number : currentPrefs.registrationNumber,
        accreditation: data.hospital_accreditation !== undefined ? data.hospital_accreditation : currentPrefs.accreditation,
        language: data.language || currentPrefs.language || "en",
        timezone: data.timezone || currentPrefs.timezone || "UTC+05:30",
      };
      this.saveHospitalPreferences(updatedPrefs);

      // 2. OPD Scheduling Settings
      const currentSched = this.getSchedulingSettings();
      let updatedSched: OPDSchedulingSettings = { ...currentSched };
      if (data.opd_scheduling_settings) {
        try {
          const parsed = typeof data.opd_scheduling_settings === "string"
            ? JSON.parse(data.opd_scheduling_settings)
            : data.opd_scheduling_settings;
          updatedSched = { ...updatedSched, ...parsed };
        } catch (err) {
          console.warn("Could not parse opd_scheduling_settings JSON:", err);
        }
      }
      if (data.default_slot_duration_minutes) {
        updatedSched.defaultSlotDurationMinutes = parseInt(data.default_slot_duration_minutes) || updatedSched.defaultSlotDurationMinutes;
      }
      if (data.opd_default_appointment_type) {
        updatedSched.defaultAppointmentType = data.opd_default_appointment_type;
      }
      if (data.opd_default_channel) {
        updatedSched.defaultChannel = data.opd_default_channel;
      }
      if (data.opd_auto_assign_rooms !== undefined) {
        updatedSched.autoAssignRooms = data.opd_auto_assign_rooms === "true" || data.opd_auto_assign_rooms === true;
      }
      if (data.opd_enable_slot_generation !== undefined) {
        updatedSched.enableSlotGeneration = data.opd_enable_slot_generation === "true" || data.opd_enable_slot_generation === true;
      }
      this.saveSchedulingSettings(updatedSched);

      // 3. Clinical Chief Complaints Library (Merge defaults so all 17+ specialties and symptoms are always present)
      let updatedComplaints: DepartmentComplaintsConfig = { ...DEFAULT_DEPARTMENT_COMPLAINTS };
      if (data.clinical_complaints) {
        try {
          const parsed = typeof data.clinical_complaints === "string"
            ? JSON.parse(data.clinical_complaints)
            : data.clinical_complaints;
          if (parsed && typeof parsed === "object" && Object.keys(parsed).length > 0) {
            updatedComplaints = {
              ...DEFAULT_DEPARTMENT_COMPLAINTS,
              ...parsed,
            };
            // Ensure every specialty retains all standard clinical symptoms plus user-added ones
            for (const [k, v] of Object.entries(DEFAULT_DEPARTMENT_COMPLAINTS)) {
              if (parsed[k]?.complaints && Array.isArray(parsed[k].complaints)) {
                updatedComplaints[k] = {
                  ...v,
                  ...parsed[k],
                  complaints: Array.from(new Set([...v.complaints, ...parsed[k].complaints])),
                };
              }
            }
          }
        } catch (err) {
          console.warn("Could not parse clinical_complaints JSON from database:", err);
        }
      }
      this.saveComplaints(updatedComplaints);

      // 4. OPD encounter / billing policy (dotted keys)
      const num = (v: any, d: number) => {
        const n = Number(v);
        return Number.isFinite(n) ? n : d;
      };
      const billingPolicy: OpdBillingPolicySettings = {
        prescriptionWindowDays: num(
          data["prescription.documentation_window_days"],
          DEFAULT_OPD_BILLING_POLICY.prescriptionWindowDays,
        ),
        followupWindowDays: num(
          data["followup.window_days"],
          DEFAULT_OPD_BILLING_POLICY.followupWindowDays,
        ),
        followupFeePercent: num(
          data["followup.fee_percent"],
          DEFAULT_OPD_BILLING_POLICY.followupFeePercent,
        ),
        followupMaxCount: num(
          data["followup.max_count"],
          DEFAULT_OPD_BILLING_POLICY.followupMaxCount,
        ),
        followupSameDoctorOnly:
          data["followup.same_doctor_only"] === undefined
            ? DEFAULT_OPD_BILLING_POLICY.followupSameDoctorOnly
            : data["followup.same_doctor_only"] === "true" ||
              data["followup.same_doctor_only"] === true,
        dialysisDefaultDoctorId: Number(data["dialysis.default_doctor_id"]) || null,
        dialysisRequireClearance:
          data["dialysis.require_clearance"] === "true" || data["dialysis.require_clearance"] === true,
      };

      let publicBooking = DEFAULT_PUBLIC_BOOKING_OPTIONS;
      if (data.public_booking_options) {
        try {
          const parsed = typeof data.public_booking_options === "string"
            ? JSON.parse(data.public_booking_options)
            : data.public_booking_options;
          const validModes = ["Physical", "Online"];
          const validCategories = ["Consultation", "Follow-up", "Routine Checkup", "Emergency", "Other"];
          publicBooking = {
            visitModes: Array.isArray(parsed.visitModes)
              ? parsed.visitModes.filter((value: string) => validModes.includes(value))
              : DEFAULT_PUBLIC_BOOKING_OPTIONS.visitModes,
            appointmentCategories: Array.isArray(parsed.appointmentCategories)
              ? parsed.appointmentCategories.filter((value: string) => validCategories.includes(value))
              : DEFAULT_PUBLIC_BOOKING_OPTIONS.appointmentCategories,
            doctorSelection: parsed.doctorSelection === "first_available" ? "first_available" : "patient_choice",
            showDepartmentSelection: typeof parsed.showDepartmentSelection === "boolean"
              ? parsed.showDepartmentSelection
              : true,
          };
          if (!publicBooking.visitModes.length) publicBooking.visitModes = ["Physical"];
          if (!publicBooking.appointmentCategories.length) publicBooking.appointmentCategories = ["Consultation"];
        } catch (err) {
          console.warn("Could not parse public_booking_options JSON:", err);
        }
      }

      return {
        preferences: updatedPrefs,
        scheduling: updatedSched,
        billingPolicy,
        complaints: updatedComplaints,
        publicBooking,
      };
    } catch (e) {
      console.warn("Could not fetch remote tenant settings from /settings:", e);
      return {
        preferences: this.getHospitalPreferences(),
        scheduling: this.getSchedulingSettings(),
        billingPolicy: DEFAULT_OPD_BILLING_POLICY,
        complaints: this.getAllComplaints(),
        publicBooking: DEFAULT_PUBLIC_BOOKING_OPTIONS,
      };
    }
  }

  /**
   * Persist hospital settings directly to the tenant database table `settings`
   */
  public async saveHospitalPreferencesRemote(prefs: HospitalPreferences): Promise<void> {
    this.saveHospitalPreferences(prefs);
    try {
      await apiService.put("/settings", {
        currency: prefs.currency,
        currency_symbol: prefs.currency,
        date_format: prefs.dateFormat,
        time_format: prefs.timeFormat,
        hospital_name: prefs.hospitalName,
        hospital_tagline: prefs.tagline || "",
        hospital_logo_url: prefs.logoUrl || "",
        hospital_address: prefs.address || "",
        hospital_city: prefs.city || "",
        hospital_state: prefs.state || "",
        hospital_postal_code: prefs.postalCode || "",
        hospital_country: prefs.country || "",
        hospital_phone: prefs.phone || "",
        hospital_emergency_phone: prefs.emergencyPhone || "",
        hospital_email: prefs.email || "",
        hospital_website: prefs.website || "",
        hospital_registration_number: prefs.registrationNumber || "",
        hospital_accreditation: prefs.accreditation || "",
        language: prefs.language,
        timezone: prefs.timezone,
        hospital_preferences: JSON.stringify(prefs),
      });
    } catch (e) {
      console.error("Error saving hospital settings to tenant database:", e);
      throw e;
    }
  }
}

export const clinicalSettingsService = new ClinicalSettingsService();
export default clinicalSettingsService;
